import { FieldSet, FormInput } from '@common/components/forms';
import { FormSelect } from '@common/components/forms/controls/FormSelect';
import { YesNoConfirmModal } from '@common/components/modals';
import { SuspenseButton } from '@common/components/utility/SuspenseButton';
import { HospitalScheduleRequestModel } from '@common/models';
import { HospitalScheduleVeterinarianModel } from '@common/models/Schedule/HospitalScheduleVeterinarianModel';
import { HospitalService } from '@common/services';
import { HospitalScheduleStore } from '@hospital/stores';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useIsMounted } from '../useIsMounted';

export interface IAddDoctorModal {
  visible: boolean;
  onSave: (veterinarianId: number) => void;
  onCancel: () => void;
}
enum AddDoctorType {
  invalid = -1,
  pickupShift = 0,
  scheduleDay = 1,
}
export interface IAddDoctorForm {
  veterinarianId: number;
  scheduleType: AddDoctorType;
}

type useScheduleDoctorModalProps = {
  scheduleStore?: HospitalScheduleStore;
  scheduleDate: DateTime;
};

export const useScheduleDoctorModal = ({
  scheduleStore,
  scheduleDate,
}: useScheduleDoctorModalProps) => {
  const { handleSubmit, control, reset, watch } = useForm<IAddDoctorForm>();
  const [showAddDcotor, setShowAddDoctor] = useState(false);
  const [showConfirmDoctor, setShowConfirmDoctor] = useState(false);
  const [selectedSchedule, setSelectedSchedule] =
    useState<HospitalScheduleRequestModel | null>(null);
  const [isConfirmBusy, setIsConfirmBusy] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const modalId = 'addScheduledDoctorModal_Calendar';
  const isMounted = useIsMounted();

  const hasAvailableVets =
    (scheduleStore?.unscheduledLicensedVetsWithAccess.length ?? 0) > 0;

  const onSubmit = useCallback(
    (data: IAddDoctorForm) => {
      const schedule: HospitalScheduleRequestModel = {
        hospitalScheduleId: 0,
        hospitalId: scheduleStore?.hospitalId ?? 0,
        hospitalScheduleTemplateId: undefined,
        veterinarianId: data.veterinarianId,
        date: scheduleDate.toLocaleString(DateTime.DATE_SHORT),
        rowVersion: '',
        isAdHoc: data.scheduleType == AddDoctorType.pickupShift,
      };
      setIsConfirmed(false);
      setSelectedSchedule(schedule);
      setShowAddDoctor(false);
      setShowConfirmDoctor(true);
    },
    [scheduleDate, scheduleStore],
  );

  const onClose = useCallback(() => {
    setShowConfirmDoctor(false);
    setShowAddDoctor(false);
    setIsConfirmBusy(false);
    setSelectedSchedule(null);
    setTimeout(() => {
      reset();
    }, 250); //give animations time to close modal or you'll see the description change before it disappears
  }, [reset]);

  useEffect(() => {
    if (selectedSchedule && isConfirmed) {
      setIsConfirmBusy(true);
      scheduleStore
        ?.addAdhocHospitalSchedule(scheduleStore?.hospitalId, selectedSchedule)
        .then(() => {
          if (!isMounted()) return;
          scheduleStore.refresh();
        })
        .finally(() => {
          if (!isMounted()) return;
          onClose();
        });
    }
  }, [selectedSchedule, onClose, isMounted, scheduleStore, isConfirmed]);

  const selectedVerterinarianId = watch('veterinarianId', 0);
  const selectedScheduleType = watch('scheduleType', AddDoctorType.invalid);

  const confirmDescription = useMemo(() => {
    const vet = _.first(
      scheduleStore?.unscheduledLicensedVetsWithAccess.filter(
        (a) => a.veterinarianId === selectedVerterinarianId,
      ),
    );
    if (
      !selectedVerterinarianId ||
      selectedScheduleType === AddDoctorType.invalid
    )
      return <span>N/A</span>;
    return (
      <>
        <span>Are you sure you want to add </span>
        <span style={{ textDecoration: 'underline' }}>
          {vet?.titleFullName}
        </span>
        <span>
          {' '}
          to the calendar{' '}
          {selectedScheduleType == 0
            ? 'for a pick up shift?'
            : ' as a scheduled doctor day?'}
        </span>
      </>
    );
  }, [
    scheduleStore?.unscheduledLicensedVetsWithAccess,
    selectedVerterinarianId,
    selectedScheduleType,
  ]);

  const scheduleDoctorModalButton = useMemo(
    () => (
      <Button
        onClick={() => setShowAddDoctor(true)}
        variant="primary"
        className="me-3 mb-3"
      >
        {' '}
        New Doctor
      </Button>
    ),
    [setShowAddDoctor],
  );
  const scheduleDoctorModal = useMemo(() => {
    return (
      <>
        <YesNoConfirmModal
          visible={showConfirmDoctor}
          backdrop="static"
          title="Add new doctor"
          busy={isConfirmBusy}
          description={confirmDescription}
          onConfirm={() => setIsConfirmed(true)}
          onCancel={onClose}
        />
        <Modal
          id={modalId}
          className="calendar-adhoc-doctor-modal"
          size="xl"
          centered
          show={showAddDcotor}
          onHide={onClose}
          backdrop="static"
        >
          <Form
            role="form"
            onSubmit={handleSubmit(onSubmit)}
            id="appointmentDetails-form"
          >
            <Modal.Header closeButton>
              <Modal.Title>Add new doctor</Modal.Title>
            </Modal.Header>
            <Modal.Body
              className="position-static"
              style={{ overflowY: 'visible' }}
            >
              <FieldSet
                disabled={
                  scheduleStore?.unscheduledLicensedVetsWithAccess.length === 0
                }
              >
                <div className="d-flex flex-column mb-3">
                  <div className="flex-fill">
                    <FormSelect
                      label="Veterinarian"
                      fieldName="veterinarianId"
                      control={control}
                      options={scheduleStore?.unscheduledLicensedVetsWithAccess}
                      getOptionLabel={(
                        opt: HospitalScheduleVeterinarianModel,
                      ) =>
                        `Dr. ${opt.firstName} ${opt.lastName}${opt.employmentStatus !== 'A' ? ' (Inactive)' : ''}`
                      }
                      getOptionValue={(
                        opt: HospitalScheduleVeterinarianModel,
                      ) => opt.veterinarianId.toString()}
                      mapValueToOption={(veterinarianId) =>
                        _.find(
                          scheduleStore?.unscheduledLicensedVetsWithAccess,
                          { veterinarianId },
                        )
                      }
                      mapOptionToValue={(opt) => opt?.veterinarianId}
                      isRequired={true}
                    />
                  </div>
                </div>
                {hasAvailableVets && (
                  <>
                    <div className="d-flex flex-column">
                      <div className="flex-fill">
                        <FormInput
                          label="ASSIGN A PICK UP SHIFT"
                          type="radio"
                          tabIndex={2}
                          control={control}
                          fieldName="scheduleType"
                          value={0}
                          required
                        />
                        <div className="alert alert-info" role="alert">
                          <label
                            className="cursor-pointer"
                            htmlFor="form-assignapickupshift"
                          >
                            Add a doctor to a previously scheduled shift where
                            appointments are booked. This option will not apply
                            the doctor&apos;s preferences or open online booking
                            availability that would interfere with the existing
                            appointments.
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column">
                      <div className="flex-fill">
                        <FormInput
                          label="ADD A SCHEDULED DOCTOR DAY"
                          type="radio"
                          tabIndex={3}
                          control={control}
                          fieldName="scheduleType"
                          value={1}
                          required
                        />
                      </div>
                      <div className="alert alert-info" role="alert">
                        <label
                          className="cursor-pointer"
                          htmlFor="form-addascheduleddoctorday"
                        >
                          Add a Doctor day that will honor that Doctor&apos; s
                          existing schedule preferences. If preferences have not
                          yet been set within the Doctor&apos;s profile,
                          appointment availability will need to be provided once
                          the Doctor has been added.
                        </label>
                      </div>
                    </div>
                  </>
                )}
                {!hasAvailableVets && (
                  <div className="d-flex flex-column">
                    <div className="alert alert-info" role="alert">
                      <label
                        className="cursor-pointer"
                        htmlFor="form-addascheduleddoctorday"
                      >
                        There are no Doctors available to schedule.
                      </label>
                    </div>
                  </div>
                )}
              </FieldSet>
            </Modal.Body>
            <Modal.Footer className="container-fluid">
              <Button
                variant="light"
                className={classNames('cancel', 'text-uppercase')}
                onClick={onClose}
              >
                Cancel
              </Button>
              <SuspenseButton
                disabled={!hasAvailableVets}
                tag={HospitalService.tags.addAdhocHospitalSchedule}
                variant="primary"
                className={classNames('confirm', 'text-uppercase')}
                type="submit"
              >
                Save
              </SuspenseButton>
            </Modal.Footer>
          </Form>
        </Modal>
      </>
    );
  }, [
    control,
    handleSubmit,
    onSubmit,
    showAddDcotor,
    scheduleStore?.unscheduledLicensedVetsWithAccess,
    confirmDescription,
    onClose,
    showConfirmDoctor,
    isConfirmBusy,
    hasAvailableVets,
  ]);

  return useMemo(() => {
    return {
      scheduleDoctorModalButton,
      scheduleDoctorModal,
      showScheduleDoctorModal: showAddDcotor,
    };
  }, [scheduleDoctorModalButton, scheduleDoctorModal, showAddDcotor]);
};
